import type { MaterialSupplier } from "@/api/materialSupplierService";
import type { MaterialType } from "@/api/materialTypeService";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";

interface MaterialSupplierTableProps {
  data: MaterialSupplier[];
  isLoading?: boolean;
  matType: MaterialType[];
  canEdit?: boolean;
  rowCount: number;
  handleEdit: (row: MaterialSupplier) => () => void;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  isActive: boolean;
}

const MaterialSupplierTable = ({
  data,
  isLoading,
  matType,
  rowCount,
  canEdit = false,
  handleEdit,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  isActive,
}: MaterialSupplierTableProps) => {
  const navigate = useNavigate();
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log("Rows per page changed to:", event.target.value);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          position: "relative",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 102,
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  textAlign: "center",
                }}
              >
                Action
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 120,
                  zIndex: 102,
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                }}
              >
                Supplier Code
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 240,
                  zIndex: 102,
                  width: 200,
                  minWidth: 200,
                  maxWidth: 200,
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Supplier Name
              </TableCell>

              {matType.map((mt) => (
                <TableCell
                  key={mt.id}
                  sx={{
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                    textAlign: "center",
                    borderRight: "1px solid #e0e0e0",
                    p: 1,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {mt.material_type_name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Skeleton variant="text" height={15} animation="wave" />
                </TableCell>
                {matType.map((mt) => (
                  <TableCell
                    key={mt.id}
                    sx={{
                      width: 100,
                      minWidth: 100,
                      maxWidth: 100,
                    }}
                  >
                    <Skeleton variant="text" height={15} animation="wave" />
                  </TableCell>
                ))}
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.supplier_id}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "white",
                      zIndex: 101,
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      textAlign: "center",
                    }}
                  >
                    <Tooltip
                      title={"แก้ไข"}
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            { name: "offset", options: { offset: [0, -10] } },
                          ],
                        },
                      }}
                    >
                      <IconButton disabled={!canEdit} onClick={handleEdit(row)}>
                        <EditIcon color={canEdit ? "primary" : "disabled"} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={"รายละเอียด"}
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            { name: "offset", options: { offset: [0, -10] } },
                          ],
                        },
                      }}
                    >
                      <IconButton
                        disabled={!isActive}
                        onClick={() =>
                          navigate(
                            `/material-supplier/material-code/${row.supplier_id}`
                          )
                        }
                      >
                        <DescriptionIcon
                          color={isActive ? "primary" : "disabled"}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 120,
                      backgroundColor: "white",
                      zIndex: 101,
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                    }}
                  >
                    {row.supplier_code}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 240,
                      backgroundColor: "white",
                      zIndex: 101,
                      width: 200,
                      minWidth: 200,
                      maxWidth: 200,
                      borderRight: "2px solid #e0e0e0",
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                    }}
                  >
                    {row.supplier_name}
                  </TableCell>

                  {matType.map((mt) => {
                    const hasMaterialType = row.material_type.some(
                      (rowMt) => rowMt.material_type_id === mt.id
                    );
                    return (
                      <TableCell
                        key={mt.id}
                        sx={{
                          width: 100,
                          minWidth: 100,
                          maxWidth: 100,
                          textAlign: "center",
                          borderRight: "1px solid #e0e0e0",
                          overflow: "hidden",
                        }}
                      >
                        {hasMaterialType ? "✔️" : ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
            {rowCount === 0 && !isLoading && (
              <TableRow>
                <TableCell
                  colSpan={3 + matType.length}
                  align="center"
                  sx={{
                    height: 150,
                    verticalAlign: "middle",
                    fontWeight: 500,
                  }}
                >
                  ไม่มีข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
};

export default MaterialSupplierTable;
